/**
 * Dark-mode context. ``ThemeProvider`` (mounted by ``ShellProviders`` in
 * ``router.tsx`` — every route except the ``/auth/continue`` and
 * ``/auth/hop`` interstitials, see KAN-1207)
 * owns the ``choice`` / ``resolved`` state, keeps a localStorage mirror,
 * reconciles with the account value (``AuthMe.ui_theme``) so the
 * preference follows the user across devices, and applies the ``.dark``
 * class on <html>.
 *
 * Scope is by SHELL, not by route: dark applies on the tenant shell
 * (storefront + tenant admin) and the platform-admin console, but NOT on
 * the marketing site (``resolveShell() === "platform"``), which stays
 * light. Putting the class on <html> means portals (drawers, modals,
 * command palette) inherit it correctly.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useAuthMe, useUpdateProfile } from "@/api/queries";
import {
  readStoredTheme,
  readThemeDevicePinned,
  resolveTheme,
  systemPrefersDark,
  writeStoredTheme,
  writeThemeDevicePinned,
  type ResolvedTheme,
  type ThemeChoice,
} from "@/lib/theme";

interface ThemeContextValue {
  choice: ThemeChoice;
  resolved: ResolvedTheme;
  /**
   * ``deviceOnly`` — jen tenhle prohlížeč, bez zápisu do účtu. Dílenský
   * terminál (KAN-1974) je sdílené zařízení: volba patří tabletu, ne
   * člověku, který je zrovna přihlášený (a terminálová session na
   * ``/auth/profile`` stejně nesmí).
   */
  setChoice: (choice: ThemeChoice, options?: { deviceOnly?: boolean }) => void;
  /** False on the marketing shell, where dark mode is not offered — the
   * toggle self-hides so it never appears on a surface it can't affect. */
  themeEnabled: boolean;
}

const ThemeContext = createContext<ThemeContextValue>({
  choice: "system",
  resolved: "light",
  setChoice: () => {},
  themeEnabled: false,
});

export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => readStoredTheme());
  const [systemDark, setSystemDark] = useState<boolean>(() => systemPrefersDark());

  // Keep ``system`` live: react to OS scheme flips without a reload.
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mq) return;
    const onChange = () => setSystemDark(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const me = useAuthMe();
  const serverTheme = me.data?.ui_theme;
  const updateProfile = useUpdateProfile();

  // Adopt the account value ONCE, the first time it's known — this is how
  // a preference set on another device lands here. After that the local
  // toggle wins and pushes to the server, so a stale ``/auth/me`` refetch
  // can't revert a fresh choice.
  const initedFromServer = useRef(false);
  useEffect(() => {
    if (initedFromServer.current || serverTheme === undefined) return;
    initedFromServer.current = true;
    // Volba připnutá k zařízení (terminál) má přednost před účtem.
    if (readThemeDevicePinned()) return;
    if (serverTheme !== readStoredTheme()) {
      setChoiceState(serverTheme);
      writeStoredTheme(serverTheme);
    }
  }, [serverTheme]);

  const setChoice = useCallback(
    (next: ThemeChoice, options?: { deviceOnly?: boolean }) => {
      setChoiceState(next);
      writeStoredTheme(next);
      writeThemeDevicePinned(Boolean(options?.deviceOnly));
      // Signed-in → persist on the account so it follows the user across
      // devices. Anonymous → localStorage-only (nothing to attach to).
      if (me.data && !options?.deviceOnly) updateProfile.mutate({ ui_theme: next });
    },
    [me.data, updateProfile],
  );

  const resolved = resolveTheme(choice, systemDark);

  // Dark mode is offered on every shell — the admin console, the tenant
  // storefront AND the marketing site. Kept as a flag (rather than an
  // inline ``true``) so a single surface could opt out later without
  // re-plumbing the toggle's self-hide.
  const themeEnabled = true;
  useLayoutEffect(() => {
    document.documentElement.classList.toggle(
      "dark",
      themeEnabled && resolved === "dark",
    );
  }, [themeEnabled, resolved]);

  return (
    <ThemeContext.Provider value={{ choice, resolved, setChoice, themeEnabled }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
